import { AppData, Colors, Lightning } from '@lightningjs/sdk';
import { SpeechType } from '@lightningjs/ui-components';
import { getFontFaceFromStyle } from 'support/textUtils';
import { reportButtonClick } from 'services/analytics/reportingServicePage';
import { ButtonType } from 'services/analytics/Conviva';
import { HoverableListItem } from 'components/common/HoverableListItem';
import { constants } from 'aliases';
import TextLabel from '../TextLabel';

const DEFAULT_PADDING_W = 48;

const DEFAULT_WIDTH = 290;
const DEFAULT_HEIGHT = 100;
const DEFAULT_FONT_SIZE = 23;
const ACTIVE_RADIUS = [51.12, 0, 51.12, 42.66]; // [tl, tr, bl, br]
const DEFAULT_BORDER_WIDTH = 4;
const SWEEP_ANIMATION_DURATION = 0.3;

export interface BezelButtonTemplateSpec
  extends Lightning.Component.TemplateSpec {
  title: SpeechType;
  label: string;
  slug: string;
  isActive: boolean;
  action: string;
  backgroundColor: { focused?: number; unfocused?: number };
  fontColor: { focused?: number; unfocused?: number };
  reportClick: boolean;
  onLoad?: () => void;

  Background: Lightning.Component;
  SweepWrapper: {
    Sweep: typeof Lightning.Element;
  };
  Button: {
    Label: typeof TextLabel;
  };
}

export default class BezelButton
  extends HoverableListItem<BezelButtonTemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<BezelButtonTemplateSpec>
{
  private _title: SpeechType | null = null;
  private _label = '';
  private _slug = '';
  private _onLoad: BezelButtonTemplateSpec['onLoad'];
  private _action = '';
  private _backgroundColor = {
    focused: Colors('appAccent').get(),
    unfocused: Colors('transparent').get(),
  };

  private _fontColor = {
    focused: Colors('activeText').get(),
    unfocused: Colors('text').get(),
  };

  /** Set this variable to override reporting click events */
  reportClick = true;
  isActive = false;

  /** Class name, should be overwritten by extending classes */
  protected _buttonClass = 'BezelButton';

  protected _SweepWrapper = this.getByRef('SweepWrapper')!;
  protected _Background = this.getByRef('Background')!;
  protected _Button = this.getByRef('Button')!;
  protected _Label = this._Button.getByRef('Label')!;

  static get height() {
    return DEFAULT_HEIGHT;
  }

  static get width() {
    return DEFAULT_WIDTH;
  }

  get title() {
    return this._title ?? this.label;
  }

  set title(title: SpeechType) {
    this._title = title;
  }

  get slug() {
    return this._slug;
  }

  set slug(slug) {
    this._slug = slug;
  }

  get label() {
    return this._label;
  }

  set label(value: string) {
    this._label = value;
    this._Label.patch({ text: { text: value } });
  }

  get onLoad() {
    return this._onLoad;
  }

  set onLoad(onLoad: BezelButtonTemplateSpec['onLoad']) {
    this._onLoad = onLoad;
  }

  set action(value: string) {
    this._action = value;
  }

  set backgroundColor(value: { focused?: number; unfocused?: number }) {
    this._backgroundColor = {
      focused: value.focused ?? this._backgroundColor.focused,
      unfocused: value.unfocused ?? this._backgroundColor.unfocused,
    };

    if (this.hasFocus()) {
      this.patch({ color: this._backgroundColor.focused });
    } else {
      this.patch({ color: this._backgroundColor.unfocused });
    }
  }

  set fontColor(value: { focused?: number; unfocused?: number }) {
    this._fontColor = {
      focused: value.focused ?? this._fontColor.focused,
      unfocused: value.unfocused ?? this._fontColor.unfocused,
    };

    if (this.hasFocus()) {
      this._Label.patch({ text: { textColor: this._fontColor.focused } });
    } else {
      this._Label.patch({ text: { textColor: this._fontColor.unfocused } });
    }
  }

  static override _template(): Lightning.Component.Template<BezelButtonTemplateSpec> {
    return {
      w: DEFAULT_WIDTH + DEFAULT_BORDER_WIDTH,
      h: DEFAULT_HEIGHT,
      rect: true,
      color: Colors('transparent').get(),
      clipping: true, // to hide transposed sweep
      SweepWrapper: {
        w: 0, // Adjust width to simulate sweep
        h: DEFAULT_HEIGHT,
        zIndex: constants.ui.zIndex.foregroundLayer + 2,
        clipping: true,
        Sweep: {
          w: DEFAULT_WIDTH,
          h: DEFAULT_HEIGHT,
          zIndex: constants.ui.zIndex.foregroundLayer + 1,
          rect: true,
          color: Colors('appAccent').get(),
          shader: {
            type: Lightning.shaders.RoundedRectangle,
            w: DEFAULT_WIDTH,
            h: DEFAULT_HEIGHT,
            radius: ACTIVE_RADIUS,
          },
        },
      },
      Background: {
        zIndex: constants.ui.zIndex.foregroundLayer + 3,
        w: DEFAULT_WIDTH,
        h: DEFAULT_HEIGHT,
        rect: true,
        color: Colors('transparent').get(),
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          w: DEFAULT_WIDTH,
          h: DEFAULT_HEIGHT,
          stroke: DEFAULT_BORDER_WIDTH,
          radius: ACTIVE_RADIUS,
          strokeColor: Colors('appAccent').get(),
        },
      },
      Button: {
        w: DEFAULT_WIDTH - DEFAULT_PADDING_W * 2,
        h: DEFAULT_HEIGHT,
        zIndex: constants.ui.zIndex.foregroundLayer + 4,
        flex: {
          direction: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          paddingLeft: DEFAULT_PADDING_W,
          paddingRight: DEFAULT_PADDING_W,
        },
        Label: {
          type: TextLabel,
          y: 2,
          w: DEFAULT_WIDTH - DEFAULT_PADDING_W * 2,
          text: {
            fontSize: DEFAULT_FONT_SIZE,
            textAlign: 'center',
            textColor: Colors('text').get(),
            fontFace: getFontFaceFromStyle('bold'),
          },
        },
      },
    };
  }

  override _getFocused() {
    return this;
  }

  override _focus() {
    this.sweep(true);
  }

  override _unfocus() {
    this.sweep(false);
  }

  override _handleClick() {
    if (AppData?.device.hoverDisabled()) {
      return true;
    }

    // Let the tile wrapper take care of this
    return false;
  }

  private sweep(active: boolean) {
    const newWidth = active ? DEFAULT_WIDTH : 0;
    this._SweepWrapper.setSmooth('w', newWidth, {
      duration: SWEEP_ANIMATION_DURATION,
    });
  }

  private reportButtonClick = () => {
    if (this.reportClick) {
      reportButtonClick({
        elementType: ButtonType.BUTTON,
        name: this._buttonClass,
        text: this.label,
      });
    }
  };
}
